import clsx from 'clsx';
import { Icon, IconType, Tooltip } from 'copilot-design-system';
import { useEffect, useRef, useState } from 'react';

import { useContextBar } from 'src/hooks/useContextBar';

export interface NavTab<T> {
  key: T;
  label: string;
  icon: IconType;
  disabled?: boolean;
}

interface ContextBarNavTabsProps<T> {
  tabs: Array<NavTab<T>>;
  activeTabKey: T;
  onTabChange: (newValue: T) => void;
  className?: string;
}

export const ContextBarNavTabs = <T,>({
  activeTabKey,
  onTabChange,
  tabs,
  className,
}: ContextBarNavTabsProps<T>) => {
  const { open, setOpen } = useContextBar();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorTop, setIndicatorTop] = useState<number | null>(null);
  const [pressedKey, setPressedKey] = useState<string | null>(null);

  const activeIndex = tabs.findIndex((tab) => tab.key === activeTabKey);

  useEffect(() => {
    if (!open || activeIndex === -1) {
      setIndicatorTop(null);
      return;
    }

    const activeButton = buttonRefs.current.get(String(tabs[activeIndex].key));
    const container = containerRef.current;

    if (activeButton && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicatorTop(buttonRect.top - containerRect.top);
    }
  }, [open, activeIndex, tabs]);

  const handleClick = (tab: NavTab<T>) => {
    const isActive = open && activeTabKey === tab.key;

    setPressedKey(String(tab.key));
    setTimeout(() => setPressedKey(null), 600);

    if (isActive) {
      setOpen(false);
    } else {
      setOpen(true);
      onTabChange(tab.key);
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx('relative flex flex-col items-center gap-2', className)}
    >
      {open && indicatorTop !== null && (
        <div
          className="absolute left-1/2 -translate-x-1/2 w-8 h-8 bg-gray-150 rounded-md pointer-events-none transition-[top] duration-[400ms] ease-out"
          style={{ top: indicatorTop }}
        />
      )}
      {tabs.map((tab) => {
        const isActive = open && activeTabKey === tab.key;
        const isPressed = pressedKey === String(tab.key);

        return (
          <Tooltip
            key={String(tab.key)}
            content={tab.label}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
            baseOffset={2}
            showDelayDuration={100}
            disabled={open}
          >
            <button
              ref={(el) => {
                if (el) {
                  buttonRefs.current.set(String(tab.key), el);
                }
              }}
              className={clsx(
                'relative z-[1] w-8 h-8 flex items-center justify-center rounded-md cursor-pointer border-none bg-transparent transition-[background-color] duration-150 ease-out',
                isActive
                  ? 'text-primary'
                  : 'text-secondary hover:bg-secondary-hover',
                isPressed && 'animate-contextbar-drawer-press',
              )}
              onClick={() => handleClick(tab)}
              disabled={tab.disabled}
              aria-label={tab.label}
            >
              <Icon icon={tab.icon} className="w-4 h-4" />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
